import React, { useState, useEffect } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
  Grid,
  Chip,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import ApiService from '../../API/Requesthandler';
import { reverse } from 'lodash';
import { getRelativeTime } from './util';

const ArchiveFiltering = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState({
    dateFilter: 'all',
    typeFilter: 'all',
    searchText: ''
  });

  // Fetch only archived messages
  const fetchMessages = async () => {
    ApiService.get('/api/chat/get_messages/'+JSON.parse(localStorage.getItem("user-data"))['id'] + "/ARCHIVE")
      .then(res => {
        if (res.success) {
          setMessages(res.data);
        } else {
          setSnackbar({ open: true, message: 'Failed to load archived messages', severity: 'error' });
        }
        setLoading(false);
      })
      .catch(err => {
        setSnackbar({ open: true, message: 'Failed to load archived messages', severity: 'error' });
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleCopyMessageText = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Message text copied to clipboard!', severity: 'success' });
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const filteredMessages = messages.filter(message => {
    const matchesDateFilter = filters.dateFilter === 'all' || true; // Add your date logic here if needed
    const matchesTypeFilter = filters.typeFilter === 'all' || message.type === filters.typeFilter;
    const matchesSearchText = message.message_text.toLowerCase().includes(filters.searchText.toLowerCase());
    return matchesDateFilter && matchesTypeFilter && matchesSearchText;
  });

  return (
    <Container maxWidth="xxl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>Archived Messages</Typography>

      {/* Filters and Search Bar */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>Date Filter</InputLabel>
            <Select
              value={filters.dateFilter}
              label="Date Filter"
              onChange={(e) => handleFilterChange('dateFilter', e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="year">This Year</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>Type Filter</InputLabel>
            <Select
              value={filters.typeFilter}
              label="Type Filter"
              onChange={(e) => handleFilterChange('typeFilter', e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="INSTANT">Instant</MenuItem>
              <MenuItem value="SCHEDULE">Schedule</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Search Message Text"
            value={filters.searchText}
            onChange={(e) => handleFilterChange('searchText', e.target.value)}
          />
        </Grid>
      </Grid>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sent from</TableCell>
              <TableCell>Sent By</TableCell>
              <TableCell>Sent to</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Trigger Event</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : (
              filteredMessages.map((message) => (
                <TableRow key={message.id}>
                  <TableCell>{message.sender}</TableCell>
                  <TableCell>{message.send_by}</TableCell>
                  <TableCell>
                    <Chip
                      label={message.recipients.length + " users"}
                      color="default"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {message.message_text}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={message.type}
                      color={message.type === 'INSTANT' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={message.status} color="default" />
                  </TableCell>
                  <TableCell>{getRelativeTime(message.date_time)}</TableCell>
                  <TableCell>
                    <Tooltip title="Copy Message Text">
                      <IconButton onClick={() => handleCopyMessageText(message.message_text)}>
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ArchiveFiltering;
